import {
  AuthWrapper,
  getCurrentFacilityId,
  userLoggedInFacilityType,
} from "@risalabs_frontend_org/auth-wrapper";
import "@risalabs_frontend_org/auth-wrapper/styles.css";
import type { FirebaseOptions } from "firebase/app";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  createBrowserRouter,
  RouterProvider,
  type RouteObject,
} from "react-router-dom";
import { firebaseApp } from "./api/firebase/firestoreService";
import AuthDataBootstrap from "./auth/AuthDataBootstrap";
import ErrorPage from "./components/errorPage/errorPage";
import { ApiBaseUrls } from "./enums/apiUrls";
import HomeVersion2 from "./pages/homeVersion2/homeVersion2";
import { RootState } from "./redux/store/store";
import { DefaultRouteConfig } from "./routes/defaultConfig";
import { ExternalWorklistRouteConfig } from "./routes/externalWorklistRouteConfig";
import { LegacyExternalWorklistRouteConfig } from "./routes/legacyExternalWorklistRouteConfig";
import { PaRouteConfig } from "./routes/paRouteConfig";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "./store/UserGeneralInfoStore";
import { getEnv } from "./utils/runtimeEnv";

/**
 * Resolve the auth-wrapper configuration from dashboard runtime environment values.
 *
 * @returns AuthWrapper configuration object used by the shared authentication shell
 */
const getAuthWrapperConfig = () => ({
  apiUrls: {
    authUrl: getEnv("REACT_APP_AUTH_URL"),
    backendServiceUrl: ApiBaseUrls.BACKEND_SERVICE,
  },
  authRedirectPath: "/auth",
  bearerAuth: {
    password: getEnv("REACT_APP_PASSWORD"),
    username: getEnv("REACT_APP_USERNAME"),
  },
  firebaseConfig: firebaseApp.options as FirebaseOptions,
  version: getEnv("REACT_APP_VERSION"),
});

/**
 * Resolve route configuration based on facility type and legacy toggle.
 *
 * @param hasAuthenticatedUser - Whether an authenticated user exists in Redux auth slice
 * @returns Route config array for the current user's facility context
 */
const resolveRouteConfig = (
  hasAuthenticatedUser: boolean,
): RouteObject[] | any[] => {
  if (!hasAuthenticatedUser) {
    return DefaultRouteConfig;
  }

  const viewLegacy = UserStore.getItem(UserGeneralInfoContextKeys.VIEW_LEGACY);
  const currentFacilityId = getCurrentFacilityId() ?? "";

  if (!currentFacilityId) {
    return PaRouteConfig;
  }

  const facilityType = userLoggedInFacilityType(currentFacilityId);
  switch (facilityType) {
    case "external":
      return viewLegacy === true
        ? LegacyExternalWorklistRouteConfig
        : ExternalWorklistRouteConfig;
    case "internal":
      return PaRouteConfig;
    case "not_assigned":
    default:
      return PaRouteConfig;
  }
};

/**
 * Bootstrap the MedOnc dashboard using the shared auth-wrapper shell.
 *
 * Uses {@link createBrowserRouter} and {@link RouterProvider} so route-level hooks
 * such as `useBlocker` receive a data router context. AuthWrapper and AuthDataBootstrap
 * wrap the layout route (same placement as the prior BrowserRouter tree).
 *
 * @returns The dashboard root with a data router
 */
const App = () => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const authWrapperConfig = useMemo(() => getAuthWrapperConfig(), []);
  const routeConfig = useMemo(
    () => resolveRouteConfig(Boolean(user?.id)),
    [user?.id],
  );

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: "/",
          element: (
            <AuthWrapper config={authWrapperConfig}>
              <AuthDataBootstrap>
                <HomeVersion2 />
              </AuthDataBootstrap>
            </AuthWrapper>
          ),
          children: routeConfig as RouteObject[],
        },
        {
          path: "*",
          element: (
            <AuthWrapper config={authWrapperConfig}>
              <AuthDataBootstrap>
                <ErrorPage />
              </AuthDataBootstrap>
            </AuthWrapper>
          ),
        },
      ]),
    [authWrapperConfig, routeConfig],
  );

  return <RouterProvider router={router} />;
};

export default App;
